import React, { Component } from 'react';
import queryString from 'query-string';
import { FormattedMessage, IntlProvider } from 'react-intl';
import {
  EDSApplication,
  HeaderBar,
  HeaderBarLogo,
  Logo,
  Typography,
  Footer,
} from '@ellucian/react-design-system/core/es';
import { createEDSTheme } from '@ellucian/react-design-system/core/es/themes';
import { IconSprite } from '@ellucian/ds-icons/lib';
import LoginForm from './LoginForm';
import ctaColor from '@ellucian/react-design-system/core/themes/common/ctaColors';
import validUrl from 'valid-url';
import PropTypes from 'prop-types';
import { withStyles } from '@ellucian/react-design-system/core/es/styles';
import {
  spacing40,
} from '@ellucian/react-design-system/core/es/styles/tokens';
import PageLoader from '../dashboardApp/components/PageLoader';
import { Route, Routes } from 'react-router-dom';
import ErrorPage from '../errorPage/ErrorPage';


const styles = {
  customLogo: {
    padding: '0.85rem',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  logo: {
    marginTop: '-1rem',
    width: `calc(5.625rem + ${spacing40} + ${spacing40})`,
    '@supports (padding-top: env(safe-area-inset-left))': {
      width: `calc(5.625rem + ${spacing40} + ${spacing40} + env(safe-area-inset-left))`,
    },
  },
};

const parsedQuery = queryString.parse(window.location.search);

class LoginApp extends Component {
  constructor(props) {
    super(props);

    this.state = {
      theme: createEDSTheme('light'),
      customLogoUrl: null,
      isThemeLoaded: false,
    };
  }

  componentDidMount() {
    this.configureTheme();
  }

  isColorValid(color) {
    const hexRegex = new RegExp(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);

    if (hexRegex.test(color)) {
      return true;
    } else if (!color) {
      return true;
    } else {
      return false;
    }
  }

  configureTheme = () => {
    const configuredOverrides = { palette: {} };
    const primaryColor = this.props.settings['theme.color.primary'];
    const secondaryColor = this.props.settings['theme.color.secondary'];
    const tertiaryName = this.props.settings['theme.color.tertiaryName'];
    const customLogoUrl = this.props.settings['theme.url.logo'];
    if (validUrl.isWebUri(customLogoUrl)) {
      this.setState({ customLogoUrl });
    }
    else {
      console.log('Ignoring invalid customLogoUrl: ' + customLogoUrl);
    }
    if (primaryColor && primaryColor.length > 0) {
      if (this.isColorValid(primaryColor)) {
        configuredOverrides.palette.primary = { main: primaryColor };
      }
      else {
        console.log('Ignoring invalid primaryColor: ' + primaryColor);
      }
    }
    if (secondaryColor && secondaryColor.length > 0) {
      if (this.isColorValid(secondaryColor)) {
        configuredOverrides.palette.secondary = { main: secondaryColor };
      }
      else {
        console.log('Ignoring invalid secondaryColor: ' + primaryColor);
      }
    }
    if (tertiaryName && tertiaryName.length > 0) {
      configuredOverrides.palette.ctaColorName = tertiaryName;
    }
    this.updateTheme(configuredOverrides);
  };

  updateTheme = (overrides) => {
    if (overrides === 'default') {
      this.setState({
        theme: createEDSTheme('light'),
      });
      return;
    }
    const configuredOverrides = { palette: {} };
    if (overrides.palette.primary) {
      configuredOverrides.palette.primary = { main: overrides.palette.primary.main };
    }
    if (overrides.palette.secondary) {
      configuredOverrides.palette.secondary = { main: overrides.palette.secondary.main };
    }
    if (overrides.palette.ctaColorName) {
      configuredOverrides.palette.ctaColor = { ...ctaColor[overrides.palette.ctaColorName.toLowerCase()] };
    }
    const newTheme = createEDSTheme('light', configuredOverrides);
    this.setState({ theme: newTheme, isThemeLoaded: true });
  };

  getAccessDenied = () => {
    return (
      <div>
        <Typography align="center" variant="h1" gutterBottom>
          <FormattedMessage id="dash.login.accessDenied.title" />
        </Typography>
        <Typography align="center" variant="body1">
          <FormattedMessage id="dash.login.accessDenied.header" />
        </Typography>
        <Typography align="center" variant="body1">
          <a href="./logout">
            <FormattedMessage id="dash.login.accessDenied.login" />
          </a>
        </Typography>
      </div>
    );
  };
  getLogout = () => {
    return (
      <div>
        <Typography align="center" variant="h1" gutterBottom>
          <FormattedMessage id="dash.login.logout.title" />
        </Typography>
        <Typography align="center" variant="h3">
          <p><FormattedMessage id="dash.login.logout.header" /></p>
        </Typography>
        <Typography align="center" variant="body1">
          <a id="signInAgain" href="." target="_self">
            <FormattedMessage id="dash.login.logout.login" />
          </a>
        </Typography>
      </div>
    );
  };

  getRoutes = () => {
    return (
      <Routes>
        <Route
          path="/login.html"
          element={<LoginForm hasLoginError={parsedQuery.error} />}
          errorElement={<ErrorPage errorCode="404" noAction={false} />}
        />
        <Route
          path="/accessDenied.html"
          element={this.getAccessDenied()}
          errorElement={<ErrorPage errorCode="404" noAction={false} />}
        />
        <Route
          path="/logout.html"
          element={this.getLogout()}
          errorElement={<ErrorPage errorCode="404" noAction={false} />}
        />
      </Routes>
    );
  };

  render() {
    const { classes } = this.props;
    let children;
    const messages = this.props.messages;
    if (messages && this.state.isThemeLoaded) {
      children = (
        <IntlProvider locale="en" messages={messages}>
          <EDSApplication locale="en"
            theme={this.state.theme}
            labelOverrides={{
              'component.Logo.logo': messages['dash.login.component.Logo.logo'],
              'component.TextField.passwordHide': messages['dash.login.component.TextField.passwordHide'],
              'component.TextField.passwordShow': messages['dash.login.component.TextField.passwordShow'],
              'component.TextField.showPassword': messages['dash.login.component.TextField.showPassword'],
              'component.TextField.hidePassword': messages['dash.login.component.TextField.hidePassword'],
              'component.TextField.toggleVisibility': messages['dash.login.component.TextField.toggleVisibility'],
            }}
          >
            <IconSprite />
            <HeaderBar position="fixed" id={'HeaderBar'}
              SearchTextFieldProps={{inputProps: {'aria-label': this.props.messages['dash.login.header']}}}
              logo={
                <HeaderBarLogo>
                  {this.state.customLogoUrl
                    ? (
                      <img src={this.state.customLogoUrl} className={classes.customLogo}
                        alt={this.props.messages['dash.navigation.degreeworks']}
                      />
                    )
                    : (
                      <Logo classes={{ root: classes.logo }} />
                    )}
                </HeaderBarLogo>
              }>
            </HeaderBar>
            {this.getRoutes()}
            <Footer footerText={this.props.messages['dash.login.footer']} />
          </EDSApplication>
        </IntlProvider>
      );
    } else {
      children = (<PageLoader />);
    }

    return <div>{children}</div>;
  }
}

LoginApp.propTypes = {
  classes: PropTypes.object,
  settings: PropTypes.object,
  messages: PropTypes.object,
};

export default withStyles(styles)(LoginApp);
